import React, { useContext, useEffect, useRef, useState } from 'react';
import { CountdownStepDefinition, SurveyDefinition } from '../../../types/Survey';
import SurveyMarkdown from '../Components/SurveyMarkdown/SurveyMarkdown';
import LocalizedText from '../../presentational/LocalizedText/LocalizedText';
import { getTextStyles } from '../Util/surveyStyles';
import { buildStepResult } from '../Util/surveyStepUtils';
import { StepResultModel } from '../../../types/SurveyResult';
import { SurveyStepContext } from '../Util/surveyStepContext';

export interface CountdownStepProps {
    step: CountdownStepDefinition;
    surveyDefinition: SurveyDefinition;
    onContinue: (stepResult: StepResultModel) => void;
    presentedDate?: string;
}

function calculateCirclePoint(centerX: number, centerY: number, radius: number, degree: number) {
    const radians = (degree - 90) * 2 * Math.PI / 360.0;
    return {
        x: centerX + radius * Math.cos(radians),
        y: centerY + radius * Math.sin(radians),
    };
}

function createCirclePath(centerX: number, centerY: number, radius: number, startDegree: number, endDegree: number): string {
    const startPoint = calculateCirclePoint(centerX, centerY, radius, endDegree);
    const endPoint = calculateCirclePoint(centerX, centerY, radius, startDegree);
    const largeArcFlag = endDegree - startDegree <= 180 ? 0 : 1;
    return `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${endPoint.x} ${endPoint.y}`;
}

export default function CountdownStep(props: CountdownStepProps) {
    const { step, surveyDefinition, onContinue, presentedDate } = props;
    const { mode } = useContext(SurveyStepContext) ?? { mode: 'Survey' as const };

    const duration = step.stepDuration ?? 5;
    const startDate = presentedDate ?? new Date().toISOString();

    const [secondsRemaining, setSecondsRemaining] = useState(duration);
    const [circlePath, setCirclePath] = useState(() => createCirclePath(61, 61, 60, 0, 359.99));
    const startRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    const titleStyles = getTextStyles('title', surveyDefinition, step, true) as React.CSSProperties;
    const textStyles = getTextStyles('text', surveyDefinition, step, true) as React.CSSProperties;

    useEffect(() => {
        if (mode === 'Design') return;

        startRef.current = performance.now();

        function tick() {
            const elapsed = (performance.now() - startRef.current!) / 1000;
            const remaining = duration - elapsed;

            if (remaining <= 0) {
                setSecondsRemaining(0);
                setCirclePath(createCirclePath(61, 61, 60, 0, 0));
                onContinue(buildStepResult(step.identifier ?? '', startDate, []));
                return;
            }

            const degrees = (remaining / duration) * 359.99;
            setCirclePath(createCirclePath(61, 61, 60, 0, degrees));
            setSecondsRemaining(Math.ceil(remaining));
            rafRef.current = requestAnimationFrame(tick);
        }

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="current-step instruction-step countdown-step">
            <div className="step-content">
                <div className="instruction-step-text">
                    {step.title && (
                        <div className="step-title" style={titleStyles}>
                            <SurveyMarkdown text={step.title} inline />
                        </div>
                    )}
                    {step.text && (
                        <div className="step-text" style={textStyles}>
                            <SurveyMarkdown text={step.text} />
                        </div>
                    )}
                </div>
                <div className="step-text">
                    <LocalizedText textKey="count-down-step-text" />
                </div>
                <div className="countdown-indicator">
                    <svg height="122" width="122">
                        <path
                            d={circlePath}
                            style={{ stroke: "var(--survey-accent-color)" }}
                            strokeWidth="1"
                            fill="#fff"
                            fillOpacity="0"
                            transform="scale(-1, 1) translate(-122, 0)"
                        />
                        <text
                            x="50%"
                            y="50%"
                            alignmentBaseline="central"
                            textAnchor="middle"
                            fontSize="50px"
                            style={{ fill: "var(--survey-text-color-0)" }}
                        >
                            {secondsRemaining}
                        </text>
                    </svg>
                </div>
            </div>
        </div>
    );
}
